import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import {
  getEnumName,
  PermissionName,
  Permissions,
  queries,
  service,
  useAuthConfig,
} from "@theprelude/core";
import {
  Button,
  Form,
  HelpIcon,
  InputFormField,
  notifyError,
  notifySuccess,
  SelectFormField,
} from "@theprelude/ds";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useOverlay } from "@/hooks/use-overlay";
import { DefaultOverlay } from "../default-overlay";

type AllowedPermission = PermissionName & ("ADMIN" | "BUILD" | "EXECUTIVE");

const permsDescriptions: Record<AllowedPermission, string> = {
  ADMIN: "This user will have full access to all capabilities within Prelude.",
  BUILD:
    "This user will have read access to the executive dashboard and read/write access to all Build actions in the CLI.",
  EXECUTIVE:
    "This user will have read access to the Prelude executive dashboard.",
};

type SignInMethod = "password" | "google" | "oidc";

type InviteUserForm = {
  email: string;
  name?: string;
  permission: AllowedPermission;
  signInMethod: SignInMethod;
};

export const inviteUserSchema = z.tuple([z.literal("invite-user")]);

function signInMethodToOIDC(signInMethod: SignInMethod, accountId: string) {
  if (signInMethod === "password") {
    return "";
  }

  if (signInMethod === "oidc") {
    return accountId;
  }

  return signInMethod;
}

const InviteUserOverlay = () => {
  const overlay = useOverlay();
  const authConfig = useAuthConfig();

  const { data: isOIDCEnabled } = useSuspenseQuery({
    ...queries.iam.getAccount(authConfig),
    select: (data) => data.oidc?.client_id !== undefined,
  });

  const form = useForm<InviteUserForm>({
    defaultValues: {
      permission: "ADMIN",
      signInMethod: "password",
    },
  });

  const { register, watch } = form;

  const inviteUser = useMutation({
    mutationFn: async (data: InviteUserForm) => {
      const accountId = authConfig.credentials?.account;

      if (!accountId) {
        throw new Error("No account ID found");
      }

      return service(authConfig).iam.inviteUser({
        email: data.email,
        name: data.name ?? "",
        permission: getEnumName(Permissions, Permissions[data.permission]),
        oidc: signInMethodToOIDC(data.signInMethod, accountId),
      });
    },
  });

  const submit = (data: InviteUserForm) => {
    inviteUser.mutate(data, {
      onSuccess: () => {
        notifySuccess({
          message:
            "User invited successfully. The email invitation has been sent.",
          dismissible: false,
        });

        void overlay.close();
      },
      onError: (error) => {
        notifyError({
          message: "Failed to invite user",
          dismissible: false,
          error,
        });
      },
    });
  };

  return (
    <DefaultOverlay title={"Invite user to Prelude"}>
      <div className="p-4">
        <Form
          {...form}
          onSubmitValid={submit}
          className="flex max-w-md flex-col gap-4"
        >
          <SelectFormField
            {...register("permission")}
            label="Permissions"
            required
            options={[
              { label: "Administrator", value: "ADMIN" },
              { label: "Build", value: "BUILD" },
              { label: "Executive", value: "EXECUTIVE" },
            ]}
          />

          <div className="flex items-center gap-2">
            <HelpIcon className="flex-shrink-0 text-secondary-foreground" />
            <p>
              {permsDescriptions[watch("permission")]}{" "}
              <a
                href="https://support.preludesecurity.com/docs/permissions"
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-2"
              >
                Learn more
              </a>
            </p>
          </div>

          <InputFormField
            {...register("email")}
            label="Email"
            type="email"
            required
            placeholder="placeholder@email.com"
          />

          <SelectFormField
            {...register("signInMethod")}
            label="Sign in method"
            required
            options={[
              { label: "Password", value: "password" },
              { label: "Google", value: "google" },
              ...(isOIDCEnabled
                ? [{ label: "OpenID Connect", value: "oidc" }]
                : []),
            ]}
          />

          <InputFormField
            {...register("name")}
            placeholder="John Doe"
            label="Full name"
          />

          <Button
            loading={inviteUser.isPending}
            disabled={inviteUser.isPending}
            iconPosition="right"
            intent="primary"
            type="submit"
          >
            Invite
          </Button>
        </Form>
      </div>
    </DefaultOverlay>
  );
};

export default InviteUserOverlay;
